import type { ICatalog } from '@platform/core/types';
import type { INavOnboardGroup } from '@platform/navigation';
import type { IOnboardGroup } from './IOnboardGroup';

/** Пакет модуля плитки: «crm/cnt» → crm, «bill» → bill. */
export const tilePackage = (key: string): string => key.split('/')[0] ?? key;

/**
 * Плитки «Створити робочий простір» (прототип v4 onbGroups): групи й модулі прототипу в його порядку, кожен — з пакетом каталогу, який
 * він установлює; модулі, яких каталог платформи не має, не показуються. Обов'язкові пакети (основні й платформні) сервер установлює в
 * кожен новий простір — їхні плитки позначені завжди. Чиста функція.
 */
export function onboardGroups(groups: readonly INavOnboardGroup[], catalog: ICatalog | undefined): IOnboardGroup[] {
  const modules = new Map((catalog?.modules ?? []).map((m) => [m.key, m]));
  return groups
    .map((g) => ({
      category: g.category,
      label: g.label,
      tiles: g.modules.flatMap((m) => {
        const pkg = modules.get(tilePackage(m.key));
        return pkg ? [{ key: m.key, module: pkg.key, label: m.label, description: m.description, required: pkg.core || pkg.platformCapability }] : [];
      }),
    }))
    .filter((g) => g.tiles.length > 0);
}

/** «N додаткових модулів вибрано» (прототип v4 onbCount): позначені плитки необов'язкових пакетів. Чиста функція. */
export function onboardCount(groups: readonly IOnboardGroup[], picked: Readonly<Record<string, boolean>>): number {
  return groups.reduce((n, g) => n + g.tiles.filter((tile) => !tile.required && picked[tile.module]).length, 0);
}

/** Пакети, позначені на екрані (необов'язкові), у порядку плиток — запит не залежить від порядку кліків. Чиста функція. */
export function onboardModules(groups: readonly IOnboardGroup[], picked: Readonly<Record<string, boolean>>): string[] {
  const keys = groups.flatMap((g) => g.tiles.filter((tile) => !tile.required && picked[tile.module]).map((tile) => tile.module));
  return [...new Set(keys)];
}
